import { Duration, LocalDate, LocalDateTime, LocalTime } from "@js-joda/core";
import React from "react";
import ReactInfiniteCalendar from "react-infinite-calendar";
import * as Ons from "react-onsenui";
import { ResceduleVisitInstanceDetails, VisitDayTimeParams } from "../messages/Visit";
import { STRINGS } from "../strings";
import { translate } from "../translate";
import { CALENDAR_SVG } from "../utils/svgs";
import { AppButton } from "./Generic/AppButton";
import { AppListItem } from "./Generic/AppList";
import { FixedTimeRangeSlider } from "./Generic/FixedTimeRangeSlider";
import { ScreenSkeleton } from "./ScreenSkeleton";
import { simpleAlert } from "./SimpleAlert";
import { localDateTimeToHtmlInputValue } from "./Visits/Request/ScheduledVisitRescheduleScreen";

interface RescheduleScreenProps {
  params: ResceduleVisitInstanceDetails;
  patientPhoneNumber: string | null;
  onChosenDate: (startTime: LocalDateTime, endTime: LocalDateTime) => Promise<string | null>;
  onBackClick: () => void;
}

interface RescheduleScreenState {
  selectedDate: LocalDate | null;
  selectedTime: LocalTime | null;
  isLoading: boolean;
}

export function getDisabledDaysOfWeek(
  dayTimeParams: VisitDayTimeParams
): (0 | 1 | 2 | 3 | 4 | 5 | 6)[] {
  const toDisable: (0 | 1 | 2 | 3 | 4 | 5 | 6)[] = [];
  const visitDays = dayTimeParams.dayTimes.map((dayTime) => {
    return dayTime.day;
  });
  for (let i = 0; i < 7; i++) {
    if (visitDays.indexOf(i) === -1) {
      toDisable.push(i as 0 | 1 | 2 | 3 | 4 | 5 | 6);
    }
  }

  return toDisable;
}

export class RescheduleScreen extends React.Component<
  RescheduleScreenProps,
  RescheduleScreenState
> {
  constructor(props: RescheduleScreenProps) {
    super(props);

    this.state = {
      selectedDate: null,
      selectedTime: null,
      isLoading: false,
    };
  }

  onDateSelected = (dateStr: string) => {
    const date = new Date(dateStr);
    const localDate = LocalDate.of(date.getFullYear(), date.getMonth() + 1, date.getDate());
    this.setState({ selectedDate: localDate, selectedTime: null });
  };

  onTimeChange = (value: LocalTime) => {
    this.setState({ selectedTime: value });
  };

  async submit() {
    if (this.state.selectedDate !== null && this.state.selectedTime !== null) {
      const startTime = LocalDateTime.of(this.state.selectedDate, this.state.selectedTime);
      const endTime = startTime.plusMinutes(this.props.params.duration);
      this.setState({ isLoading: true });
      const result = await this.props.onChosenDate(startTime, endTime);
      this.setState({ isLoading: false });

      if (result === null) {
        this.props.onBackClick();
        simpleAlert({
          icon: "success",
          title: translate(STRINGS.SUCCESS),
          message: translate(STRINGS.RESCHEDULED_SUCCESSFULLY),
        });
      } else {
        simpleAlert({
          icon: "error",
          title: translate(STRINGS.ERROR),
          message: result,
        });
      }
    } else {
      simpleAlert({
        icon: "error",
        message: translate(STRINGS.PLEASE_SELECT_DATE_AND_TIME),
      });
    }
  }

  isReadyForSubmit = () => this.state.selectedDate !== null && this.state.selectedTime !== null;

  render() {
    const dayParam =
      this.state.selectedDate !== null && this.props.params.dayTimeParams !== null
        ? this.props.params.dayTimeParams.dayTimes[this.state.selectedDate.dayOfWeek().value()]
        : null;
    // maxDate={this.props.visitDetails.endTimeLocal === undefined ? undefined : localDateTimeToHtmlInputValue(this.props.visitDetails.endTimeLocal) as any}
    //             minDate={localDateTimeToHtmlInputValue(this.props.visitDetails.startTimeLocal) as any}
    return (
      <ScreenSkeleton
        title={translate(STRINGS.RESCHEDULE)}
        titleBarStyle={ScreenSkeleton.TitleBarStyle.PRIMARY}
        onBackClick={this.props.onBackClick}
      >
        <div className="RescheduleScreen__container">
          {this.state.selectedDate === null ? (
            <div className="RescheduleScreen__dateContainer">
              <h3 className="RescheduleScreen__dateLabel">{translate(STRINGS.SELECT_DATE)}</h3>
              <ReactInfiniteCalendar
                disabledDays={
                  this.props.params.dayTimeParams !== null
                    ? getDisabledDaysOfWeek(this.props.params.dayTimeParams)
                    : undefined
                }
                minDate={localDateTimeToHtmlInputValue(this.props.params.startTimeLocal) as any}
                maxDate={localDateTimeToHtmlInputValue(this.props.params.endTimeLocal) as any}
                max={localDateTimeToHtmlInputValue(this.props.params.endTimeLocal) as any}
                min={localDateTimeToHtmlInputValue(this.props.params.startTimeLocal) as any}
                className="VisitDetailsScreen__calendar"
                height={250}
                displayOptions={{ showOverlay: false, showHeader: false, showTodayHelper: false }}
                selected={false}
                onSelect={this.onDateSelected}
              />
            </div>
          ) : null}
          {this.state.selectedDate !== null ? (
            <div className="RescheduleScreen__timeContainer">
              <div className="RescheduleScreen__selectedDate">
                <AppListItem
                  leading={CALENDAR_SVG}
                  title={translate(STRINGS.START_DATE) + ": " + this.state.selectedDate.toString()}
                  trailing={
                    <AppButton
                      variant="outline"
                      onClick={() => this.setState({ selectedDate: null, selectedTime: null })}
                    >
                      <span>{translate(STRINGS.SELECT_DATE)}</span>
                    </AppButton>
                  }
                />
              </div>
              <h3 className="RescheduleScreen__timeLabel">{translate(STRINGS.SELECT_TIME)}</h3>
              <FixedTimeRangeSlider
                minTime={dayParam !== null ? dayParam.startTime : LocalTime.MIN}
                maxTime={dayParam !== null ? dayParam.endTime : LocalTime.MAX}
                duration={Duration.ofMinutes(this.props.params.duration)}
                minuteSteps={15}
                value={this.state.selectedTime === null ? LocalTime.of() : this.state.selectedTime}
                onChange={this.onTimeChange}
              />
            </div>
          ) : null}
          <span style={{ flexGrow: 1 }} />
          <div className="RescheduleScreen__submitContainer">
            {this.state.isLoading ? (
              <Ons.ProgressCircular indeterminate={true} />
            ) : (
              <AppButton
                className="RescheduleScreen__submit"
                variant={this.isReadyForSubmit() ? "filled" : "outline"}
                color="primary"
                onClick={() => this.submit()}
              >
                <span>{translate(STRINGS.SUBMIT)}</span>
              </AppButton>
            )}
          </div>
        </div>
      </ScreenSkeleton>
    );
  }
}
